import { useEffect, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { Editor } from 'grapesjs';
import { AppShell } from '../components/layout/AppShell';
import { useProjectsStore } from '../stores/projects-store';
import { useEditorStore } from '../stores/editor-store';
import { useAutoSave } from '../hooks/use-auto-save';

function applyCanvasConstraints(editor: Editor, opts: { width?: number; height?: number }) {
  const { width, height } = opts;
  const canvasEl = editor.Canvas.getElement();
  const frame = editor.Canvas.getFrameEl();
  if (!canvasEl || !frame) return;

  if (width && height) {
    canvasEl.style.overflow = 'auto';
    frame.style.display = 'block';
    frame.style.margin = '0 auto';
    frame.style.width = `${width}px`;
    frame.style.minWidth = `${width}px`;
    frame.style.maxWidth = `${width}px`;
    frame.style.height = `${height}px`;
    frame.style.minHeight = `${height}px`;
    frame.style.maxHeight = `${height}px`;
  } else {
    // Reset to default behavior
    canvasEl.style.overflow = '';
    frame.style.display = '';
    frame.style.margin = '';
    frame.style.width = '';
    frame.style.minWidth = '';
    frame.style.maxWidth = '';
    frame.style.height = '';
    frame.style.minHeight = '';
    frame.style.maxHeight = '';
  }
}

export function EditorPage() {
  useAutoSave();

  const navigate = useNavigate();
  const { projectId } = useParams<{ projectId: string }>();
  const projects = useProjectsStore((s) => s.projects);
  const currentProject = useProjectsStore((s) => s.currentProject);
  const setCurrentProject = useProjectsStore((s) => s.setCurrentProject);
  const loadProjectContent = useProjectsStore((s) => s.loadProjectContent);

  const editor = useEditorStore((s) => s.editorInstance);
  const loadContent = useEditorStore((s) => s.loadContent);

  const targetProject = useMemo(() => {
    if (!projectId) return null;
    return projects.find((p) => p.id === projectId) ?? null;
  }, [projects, projectId]);

  // Keep currentProject in sync with route param.
  useEffect(() => {
    if (!projectId) return;
    if (!targetProject) return;
    if (currentProject?.id === targetProject.id) return;
    setCurrentProject(targetProject);
  }, [projectId, targetProject?.id]);

  const loadedRef = useRef<string>('');
  useEffect(() => {
    if (!projectId || !editor) return;
    if (!currentProject || currentProject.id !== projectId) return;

    // Avoid re-loading the same project+version on every snapshot update.
    const versionKey = currentProject.activeContentVersionId ?? 'default';
    const loadKey = `${projectId}:${versionKey}`;
    if (loadedRef.current === loadKey) return;

    loadedRef.current = loadKey;

    (async () => {
      const content = await loadProjectContent(projectId, { versionId: currentProject.activeContentVersionId });
      loadContent(content ?? { html: '', css: '', components: [], styles: [] });

      // Ensure at least one page exists (GrapesJS pages API).
      try {
        const pages = (editor as any).Pages?.getAll?.() ?? [];
        if (pages.length === 0 && (editor as any).Pages?.add) {
          (editor as any).Pages.add({ name: 'Page 1' });
        }
      } catch {
        // ignore
      }

      applyCanvasConstraints(editor, {
        width: (currentProject as any).canvasWidth,
        height: (currentProject as any).canvasHeight,
      });
    })();
  }, [editor, projectId, currentProject?.id, currentProject?.activeContentVersionId, (currentProject as any)?.canvasWidth, (currentProject as any)?.canvasHeight]);

  // If projectId not found in list (e.g. deleted), go back to dashboard.
  useEffect(() => {
    if (!projectId) return;
    if (projects.length === 0) return;
    if (!targetProject) navigate('/');
  }, [projectId, projects.length, targetProject?.id]);

  return <AppShell />;
}

